// Call review window view-model (CU-869fmd2x2). Pure, so it is testable without the barrel.
// The server's `extracted` block (GET calls/{id}/ and lead-prefill) carries each hint with its
// source and start/end offsets into `extracted.text`.
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { formatUkPhone } from 'src/utils/ukPhone';
import { noRecordingMessage } from './recordingState';

dayjs.extend(utc);
dayjs.extend(timezone);

// Calls happen at the practice, so times are UK time whatever the browser's zone.
const UK = 'Europe/London';

// "25 Oct, 17:07" (the year only when it isn't this year); '' when unusable.
export function ukTime(iso, now = new Date()) {
  if (!iso || !dayjs(iso).isValid()) return '';
  const at = dayjs(iso).tz(UK);
  return at.format(at.year() === dayjs(now).tz(UK).year() ? 'D MMM, HH:mm' : 'D MMM YYYY, HH:mm');
}

// "4m 12s" / "45s"; '' for no duration.
export function durationText(seconds) {
  const n = Math.round(Number(seconds));
  if (!Number.isFinite(n) || n <= 0) return '';
  const m = Math.floor(n / 60);
  return m ? `${m}m ${n % 60}s` : `${n}s`;
}

export const SOURCE_TEXT = {
  elevate_summary: 'from Elevate summary',
  dentalsem_summary: 'from call summary',
  transcript: 'from transcript',
};
export const sourceText = (source) => SOURCE_TEXT[source] || '';

export const INTENT_TEXT = {
  book: 'Wants to book',
  reschedule: 'Wants to reschedule',
  cancel: 'Wants to cancel',
  enquiry: 'Enquiry',
};

// Each hint names its own source: a name the summary lacked can come from the transcript's
// caller lines, with offsets into the transcript rather than `text`.
const span = (raw, source) => (raw && typeof raw === 'object' && typeof raw.value === 'string' && raw.value.trim() ? { source, ...raw } : null);

// The server's block, or null when it found nothing (or an older backend sent none).
export function readExtracted(raw) {
  if (!raw || typeof raw !== 'object' || !raw.source || typeof raw.text !== 'string') return null;
  const block = {
    source: raw.source,
    text: raw.text,
    firstName: span(raw.first_name, raw.source),
    lastName: span(raw.last_name, raw.source),
    treatment: span(raw.treatment, raw.source),
    callbackTime: span(raw.callback_time, raw.source),
    intent: span(raw.appointment_intent, raw.source),
  };
  return block.firstName || block.treatment || block.callbackTime || block.intent ? block : null;
}

// Key details chips: Name, Treatment, Call back, Intent (only those found).
export function keyDetails(extracted) {
  if (!extracted) return [];
  const name = [extracted.firstName?.value, extracted.lastName?.value].filter(Boolean).join(' ');
  return [
    name && { key: 'name', label: 'Name', value: name, note: extracted.firstName.source !== extracted.source ? sourceText(extracted.firstName.source) : '' },
    extracted.treatment && { key: 'treatment', label: 'Treatment', value: extracted.treatment.value },
    extracted.callbackTime && { key: 'callback', label: 'Call back', value: extracted.callbackTime.value },
    extracted.intent && { key: 'intent', label: 'Intent', value: INTENT_TEXT[extracted.intent.value] || extracted.intent.value },
  ].filter(Boolean);
}

// Words worth catching the eye in a summary or transcript, besides the server's spans.
const PATTERNS = [
  ['time', /\b(?:(?:after|before|around|at|between)\s+)?\d{1,2}(?:[:.]\d{2})?\s?(?:am|pm)\b/gi],
  ['time', /\b(?:today|tomorrow|tonight|this (?:morning|afternoon|evening)|(?:mon|tues|wednes|thurs|fri|satur|sun)day|next week)\b/gi],
  ['time', /\b\d{1,2}(?:st|nd|rd|th)?\s(?:jan|feb|mar|apr|may|jun|jul|aug|sep|sept|oct|nov|dec)[a-z]*\b/gi],
  ['phone', /(?:\+44\s?|\b0)\d(?:[\s-]?\d){8,9}\b/g],
  ['booking', /\b(?:appointments?|bookings?|booked|consultations?)\b/gi],
];

// "No appointment was booked", "not booked", "didn't book": the clause before a booking word
// (back to a comma or "but", at most 40 characters) says it did NOT happen.
const NEGATION = /\b(?:no|not|never|without|didn['’]t|did not|don['’]t|won['’]t|wasn['’]t|cannot|can['’]t)\b/i;
export const negatedAt = (text, start) => NEGATION.test(text.slice(Math.max(0, start - 40), start).split(/[.!?;,\n]|\bbut\b/).pop());

// Non-overlapping [{start, end, kind}] in text order. `source` names this text
// ('elevate_summary', 'dentalsem_summary' or 'transcript'): the server's spans count only when
// they came from it (a summary's spans also need `extracted.text` to be this text). An
// enquiry ("ask about") isn't highlighted, nor is a booking word said in the negative. On
// overlap the earlier (then longer) range wins.
export function highlightRanges(text, extracted, source = extracted?.source) {
  if (typeof text !== 'string' || !text) return [];
  const ranges = [];
  if (extracted && (source === 'transcript' || extracted.text === text)) {
    [['name', extracted.firstName], ['name', extracted.lastName], ['treatment', extracted.treatment], ['time', extracted.callbackTime], ['booking', extracted.intent]]
      .forEach(([kind, item]) => {
        if (!item || item.source !== source || (kind === 'booking' && item.value === 'enquiry')) return;
        if (Number.isInteger(item.start) && Number.isInteger(item.end) && item.start < item.end && item.end <= text.length) {
          ranges.push({ start: item.start, end: item.end, kind });
        }
      });
  }
  PATTERNS.forEach(([kind, pattern]) => {
    pattern.lastIndex = 0;
    let match = pattern.exec(text);
    while (match) {
      if (match[0].trim() && !(kind === 'booking' && negatedAt(text, match.index))) {
        ranges.push({ start: match.index, end: match.index + match[0].length, kind });
      }
      match = pattern.exec(text);
    }
  });
  ranges.sort((a, b) => a.start - b.start || b.end - a.end);
  const out = [];
  ranges.forEach((range) => {
    if (!out.length || range.start >= out[out.length - 1].end) out.push(range);
  });
  return out;
}

// One line saying why there is no audio.
export function noRecordingLine(call) {
  if (call?.outcome === 'missed') return "No recording — call wasn't answered";
  if (call?.recordingReason === 'not_recorded_ring_group') return 'Not recorded';
  const message = noRecordingMessage(call);
  return message ? message.detail : 'No recording for this call';
}

// The header's one primary action: the server's choice when it made one, else by state.
const PRIMARY = { call_back: 'call_back', create_lead: 'create_lead', open_patient: 'open_patient' };
export function primaryAction(call) {
  const server = Array.isArray(call?.nextActions) ? call.nextActions.find((a) => a?.primary && PRIMARY[a.id]) : null;
  if (server) return server.id;
  if (call?.lead?.id) return 'open_patient';
  if (call?.followUpState && call.followUpState !== 'responded' && call?.outcome === 'missed') return 'call_back';
  return 'create_lead';
}
export const ACTION_TEXT = { call_back: 'Call back', create_lead: 'Create lead', open_patient: 'Open patient' };

const OUTCOME_TEXT = { answered: 'Answered', missed: 'Missed', voicemail: 'Voicemail', failed: 'Failed', busy: 'Busy' };
const DIRECTION_TEXT = { inbound: 'Incoming', outbound: 'Outgoing' };

// direction · outcome · UK time · duration · line · answered by (only the parts known).
export function metaParts(call) {
  if (!call) return [];
  // The server's "site · ring group"; an older backend: the line's number, never the connection name.
  const line = call.lineDisplay || [formatUkPhone(call.siteNumber || ''), call.huntGroup].filter(Boolean).join(' · ');
  return [
    DIRECTION_TEXT[call.direction] || '',
    OUTCOME_TEXT[call.outcome] || '',
    ukTime(call.timestamp),
    durationText(call.durationSec),
    line,
    call.userName ? `Answered by ${call.userName}` : '',
  ].filter(Boolean);
}

// Title: the lead's name, else the number the way staff say it.
export function callTitle(call) {
  if (call?.lead?.name) return { text: call.lead.name, isLead: true };
  const number = call?.numberDisplay || formatUkPhone(call?.contact || '') || '';
  return { text: number || 'Unknown caller', isLead: false };
}

// "Summary from Elevate · 25 Oct, 17:07".
export function summaryCaption(call) {
  const source = call?.aiSummarySource || call?.summarySource || '';
  const from = source === 'elevate' ? 'Summary from Elevate' : 'Summary by DentalSEM';
  const when = ukTime(call?.aiReviewGeneratedAt);
  return when ? `${from} · ${when}` : from;
}

// What to say in place of a summary (CU-869fkj6eg). The server already decides
// this; saying a flat "No summary for this call" while Elevate's own summary is
// still on its way (it takes about five minutes) reads as a dead end and sends
// people looking for a bug that isn't there.
export const SUMMARY_STATUS_TEXT = {
  waiting_for_elevate: 'Waiting for the Elevate summary — it usually arrives within a few minutes of the call.',
  queued: 'Summary on its way. This usually takes a minute or two.',
};

// Elevate sends its summary within a few minutes of the call or not at all (the
// server waits ELEVATE_SUMMARY_WAIT, 15 minutes). Past that the waiting line is no
// longer true, and a window still promising "a few minutes" about a call from this
// morning reads as broken rather than patient.
export const ELEVATE_WAIT_MINUTES = 20;

export function summaryStatusLine(call, working = false, now = new Date()) {
  if (working) return 'Reading the call and writing a summary…';
  if (call?.summaryStatus === 'waiting_for_elevate') {
    const at = call?.timestamp && dayjs(call.timestamp);
    if (at && at.isValid() && dayjs(now).diff(at, 'minute') >= ELEVATE_WAIT_MINUTES) {
      return 'Elevate did not send a summary for this call.';
    }
  }
  const text = SUMMARY_STATUS_TEXT[call?.summaryStatus];
  if (text) return text;
  if (call?.outcome === 'missed') return 'No summary: the call was not answered.';
  // We deliberately do not pay to summarise a recorded greeting and the message
  // we left (apps/voice/tasks.py, CU-869fmd3bg). Without this line the panel
  // reads as broken on a call that correctly has none; the transcript is there.
  if (call?.outcome === 'voicemail') return 'No summary: this call reached a voicemail. The transcript is below.';
  return 'No summary for this call.';
}

// "Agent: hello" → { speaker: 'Agent', start: 7 } for a transcript paragraph; null otherwise.
export function speakerOf(paragraph) {
  const match = /^([A-Z][\w .'-]{0,24}):\s+/.exec(paragraph || '');
  return match ? { speaker: match[1], start: match[0].length } : null;
}

// One line for a call with no summary, recording or transcript (and nothing extracted):
// { text, detail } where `detail` is the provider's longer reason, when it gave one.
export function nothingToReviewLine(call) {
  let reason = '';
  if (call?.isTest) reason = 'test call';
  else if (call?.outcome === 'missed') reason = "call wasn't answered";
  else if (call?.recordingReason === 'not_recorded_ring_group') reason = 'ring group calls are not recorded';
  if (reason) return { text: `No summary, recording or transcript — ${reason}`, detail: '' };
  return { text: 'No summary, recording or transcript for this call', detail: noRecordingMessage(call)?.detail || '' };
}
